// 30-Day Ignatian Lenten Journey to Flourishing
// Based on the Spiritual Exercises of St. Ignatius Loyola

export const retreatContent = {
  title: "30-Day Ignatian Lenten Journey to Flourishing",
  subtitle: "Based on the Spiritual Exercises, the 19th Annotation & The RIMP Way",
  institution: "Divine Mercy University",
  description: "A retreat in daily life integrating ancient Ignatian wisdom with modern spiritual psychology",
  logo: "https://customer-assets.emergentagent.com/job_flourish-dmu/artifacts/bxzg7jmj_Logo%20divine%20mercy.jpg",
  
  resourceLinks: {
    rcSpirituality: { name: "RC Spirituality", url: "https://rcspirituality.org/" },
    rcRetreatGuides: { name: "RC Retreat Guides", url: "https://www.regnumchristi.com/en/retreat-guides/" },
    spiritualExercises: [
      { name: "Ignatian Spirituality", url: "https://www.ignatianspirituality.com/" },
      { name: "Regnum Christi Retreats", url: "https://regnumchristi.org/en/retreats/" }
    ],
    prayerApps: [
      { name: "Hallow", url: "https://hallow.com/", description: "Catholic prayer & meditation app" },
      { 
        name: "Laudate", 
        url: "https://apps.apple.com/us/app/laudate-1-catholic-app/id499428207",
        androidUrl: "https://play.google.com/store/apps/details?id=com.aycka.apps.MassReadings",
        description: "Free Catholic app with prayers, rosary, and more" 
      },
      { name: "iBreviary", url: "https://www.ibreviary.org/", description: "Liturgy of the Hours and daily readings" }
    ],
    dmuResources: [
      { name: "Campus Ministry", email: "campusministry@divinemercy.edu", description: "Email to speak with a spiritual director" },
      { name: "Alumni Spiritual Directors", url: "https://divinemercy.edu/?taxonomy=degree-conferred&term=spiritual-direction-certificate", description: "Connect with DMU spiritual direction graduates" }
    ],
    usccb: "https://bible.usccb.org/"
  },

  // Explanation of the Four Stages (not literal weeks)
  fourStages: {
    title: "Understanding the Four 'Weeks' of the Exercises",
    intro: `The structure of St. Ignatius's Spiritual Exercises often causes confusion because Ignatius used the term "Week" to describe thematic STAGES rather than literal seven-day calendar periods. In the Spiritual Exercises [Annotation 4], Ignatius explicitly states: "By the word 'Week' is understood a certain period of time... it is not to be understood that each Week should necessarily consist of seven or eight days."`,
    stages: [
      { 
        name: "First Stage", 
        theme: "Relationship", 
        ignatianTheme: "Sin, Mercy, and God's Love",
        typicalDuration: "7–10 Days",
        description: "Encountering God's unconditional love and mercy, recognizing our need for a Savior."
      },
      { 
        name: "Second Stage", 
        theme: "Identity", 
        ignatianTheme: "The Life of Christ (Discipleship)",
        typicalDuration: "10–12 Days",
        description: "Walking with Jesus through His public ministry, discovering who we are as His disciples. This is typically the longest stage because it involves deep 'election' (discerning God's will for your life)."
      },
      { 
        name: "Third Stage", 
        theme: "Mission", 
        ignatianTheme: "The Passion and Death of Jesus",
        typicalDuration: "5–7 Days",
        description: "Accompanying Christ in His suffering, understanding the cost of discipleship and mission."
      },
      { 
        name: "Fourth Stage", 
        theme: "Purpose", 
        ignatianTheme: "The Resurrection and God's Presence",
        typicalDuration: "5–7 Days",
        description: "Encountering the Risen Lord, being sent forth with purpose and joy."
      }
    ],
    note: "Your spiritual director may shorten or lengthen each stage depending on whether you are experiencing consolation or desolation, or if you need more time to process a specific mystery of Christ's life."
  },

  introduction: {
    title: "The DMU Mission: Forming Psychological & Spiritual Missionaries",
    content: `At Divine Mercy University, we form men and women who integrate their professional gifts and personal talents with a deep interior life, bringing Christ's healing presence to a world in need.

This 30-Day Lenten Retreat is designed to help you discover YOUR unique mission. Whether you are a counselor, educator, parent, student, or professional, God has given you specific gifts and a particular calling. This retreat will help you identify how to live out your faith daily through the lens of your own God-given talents.

**UNDERSTANDING THE FORMAT: The 19th Annotation**

The Spiritual Exercises of St. Ignatius were originally designed as a 30-day silent retreat. However, Ignatius also created the "19th Annotation"—a format specifically for busy professionals, students, parents, and anyone who cannot leave their daily life for an extended retreat. In this adaptation:

• You engage with daily Scripture and reflection while continuing your normal responsibilities
• The "Four Weeks" are actually FOUR STAGES—thematic movements that may each take more or less than seven days
• You move at your own pace, allowing the Holy Spirit to guide your progress
• You may meet periodically with a spiritual director, or you can make this journey on your own

**WHAT IF I DON'T HAVE A SPIRITUAL DIRECTOR?**

Many who take this retreat may not have access to regular spiritual direction over the 30 days—and that's okay! This retreat is designed to provide you with:

• Daily Scripture and guided reflections to direct your prayer
• The "My Journey" digital journal to track your interior movements (consolations and desolations)
• Retreat guide videos from Fr. John Bartunek, LC to offer weekly guidance
• The RIMP framework to help you organize your insights about Relationship, Identity, Mission, and Purpose

Even without a director, you will gain powerful personal insights into how you can live out your particular mission gifts and grow in your daily faith.

**THE FOUR STAGES (NOT LITERAL "WEEKS")**

Ignatius used the term "Week" to describe thematic STAGES, not seven-day periods:

• **Stage 1 – RELATIONSHIP:** Sin, Mercy, and God's Love (typically 7-10 days)
• **Stage 2 – IDENTITY:** The Life of Christ and Discipleship (typically 10-12 days)
• **Stage 3 – MISSION:** The Passion and Death of Jesus (typically 5-7 days)
• **Stage 4 – PURPOSE:** The Resurrection and God's Presence (typically 5-7 days)

Don't feel pressured to complete each day in only one day. If the Holy Spirit is saying "rest and contemplate here a little longer"—listen! This retreat is for YOUR spiritual growth and YOUR mission clarity, not for checking boxes.

**RESOURCES FOR YOUR JOURNEY**

We have included "Spiritual Talks & Meditations" for each stage featuring retreat guides from Fr. John Bartunek, LC, plus "Music for Prayer & Meditation" with sacred selections to help quiet your mind and open your heart to God.

For recording your reflections, you can: (1) Use the "My Journey" digital notes feature, (2) Download and print the PDF, or (3) Use your own notebook.

For spiritual accompaniment: contact DMU Campus Ministry at campusministry@divinemercy.edu, speak with your own director, or connect with our trained alumni spiritual directors through the Alumni Directory link above, or follow up with your own spiritual director or a trusted friend.`
  },

  dailyStructure: {
    title: "Structure of the 30-Day Retreat",
    items: [
      { time: "Morning", activity: "Morning Prayer", duration: "20–30 min", note: "Read the day's Scripture and meditate on the passage. You may also listen to part of a talk/music if you have some extra time; however, personal silence and reflection is the key!" },
      { time: "Midday", activity: "Midday Examen", duration: "5 min", note: "Pause to notice God's movements, insights, and any struggles in your day.", centered: true },
      { time: "Evening", activity: "Nightly Examen", duration: "10–15 min", note: "Review your day the Ignatian Way." },
      { time: "Anytime", activity: "Extra Prayer", duration: "5–15 min", note: "Only if you have time!", optional: true, description: "Listen to part of a meditation by Fr. Bartunek or some sacred music while commuting or during some down time in your day.", subscribeLink: "https://rcspirituality.org/lent/", subscribeText: "Subscribe to Lenten Meditations" }
    ]
  },

  rimpModel: {
    title: "The RIMP Way to Human Flourishing",
    subtitle: "A Four-Stage Framework for Discovering Your Unique Mission",
    intro: "The RIMP model guides your mission journey through four stages—a theological progression that moves from knowing God → knowing yourself → serving your neighbor → fulfilling God's purpose for you. Each stage corresponds to one of the traditional 'Weeks' of the Spiritual Exercises.",
    stagesNote: "Remember: These are STAGES, not literal seven-day periods. Move at your own pace, allowing the Holy Spirit to guide your progress.",
    selfDiscoveryIntro: "St. Ignatius encouraged retreatants to use ALL their faculties—intellect, imagination, memory, and will—to know God and themselves better. Consider what you already know about yourself from past experiences, assessments, or insights.",
    pillars: [
      {
        letter: "R",
        name: "Relationship",
        greek: "Alpha (A)",
        ignatianWeek: "First Stage",
        ignatianTheme: "Sin, Mercy & God's Love",
        ignatianConnection: "Foundation Principle — God created me to praise, reverence, and serve Him. Everything starts with knowing you are in relationship with your Creator.",
        definition: "How you connect with God — He is your origin",
        description: "Before anything else, I am in relationship with God. I am created, known, loved, and sustained by Him. All flourishing begins with receiving this identity from the Father.",
        keyScripture: "\"I have called you by name: you are mine.\" — Isaiah 43:1",
        whatYouDiscover: "Core theology, how God sees you, the purpose of human existence, and why daily dialogue with God (Dia-Logos) comes first.",
        selfDiscovery: {
          title: "Explore Your Spiritual Life",
          intro: "St. Ignatius encouraged retreatants to use ALL their faculties—intellect, imagination, memory, and will—to know God and themselves better. This is YOUR area for self-discovery. Explore what resonates with your unique soul:",
          suggestions: [
            "Prayer Styles: What forms of prayer draw you closest to God? (Lectio Divina, Rosary, Contemplative Prayer, Adoration, Journaling, Walking Prayer, Imaginative Prayer, Charismatic Praise)",
            "Spiritual Gifts Inventory: Have you identified your spiritual gifts? Consider taking the Wagner-Modified Houts Questionnaire, the SHAPE assessment, or exploring the gifts listed in Romans 12, 1 Corinthians 12, and Ephesians 4.",
            "Formative Experiences: What retreats, books, pilgrimages, or spiritual encounters have shaped your faith journey? What made them powerful?",
            "Sacred Moments: When have you felt God's presence most strongly? In nature? In community? In silence? In service? In suffering?",
            "Your Spiritual Director or Mentor: Who has helped guide your spiritual growth? What wisdom have they shared?"
          ]
        },
        reflection: "How would you describe your current relationship with God? When do you feel closest to Him? What obstacles keep you from deeper connection?"
      },
      {
        letter: "I",
        name: "Identity",
        greek: "Your Design",
        ignatianWeek: "Second Stage",
        ignatianTheme: "Life of Christ (Discipleship)",
        ignatianConnection: "Call of the King — Who am I called to be in God's army? Discover how God uniquely designed you.",
        definition: "Who God made you to be — discovered, not self-constructed",
        description: "Identity is received, not achieved. My dignity comes from being a beloved son or daughter of God. Through Christ, I rediscover who I truly am and who I am becoming. God designed you with a specific temperament, specific tools (Intellect, Imagination, Will), and specific natural talents.",
        keyScripture: "\"We are his handiwork, created in Christ Jesus for good works.\" — Ephesians 2:10",
        whatYouDiscover: "Your temperament (Choleric, Sanguine, Melancholic, Phlegmatic), your natural talents, and how God uniquely designed you for His purposes.",
        selfDiscovery: {
          title: "Know Your Unique Design",
          intro: "God has uniquely designed you with specific gifts, temperament, and talents. St. Ignatius believed self-knowledge is essential for spiritual growth. Use these tools and reflections to discover who God made you to be:",
          suggestions: [
            "Temperament Assessment: Explore the Four Temperaments (Choleric, Sanguine, Melancholic, Phlegmatic). Consider resources like 'The Temperament God Gave You' by Art & Laraine Bennett.",
            "Personality Inventories: What have you learned from Myers-Briggs (MBTI), Enneagram, DISC, CliftonStrengths (StrengthsFinder), or VIA Character Strengths? How do these illuminate your path?",
            "Learning & Processing Styles: Are you visual, auditory, kinesthetic, or reading/writing oriented? Do you process internally or externally? This affects how you pray best.",
            "Natural Talents & Abilities: What comes easily to you that others find difficult? What activities make you lose track of time? What do people consistently ask you for help with?",
            "Wounds & Growth Areas: What struggles have shaped you? Sometimes our greatest gifts emerge from our deepest wounds (the 'wounded healer' concept).",
            "Cultural & Family Heritage: How has your background shaped your identity? What traditions, values, and faith practices did you inherit?"
          ]
        },
        reflection: "What do people consistently ask for your help with? What activities make you lose track of time? What breaks your heart when you see it in the world?"
      },
      {
        letter: "M",
        name: "Mission",
        greek: "Your Equipment",
        ignatianWeek: "Third Stage",
        ignatianTheme: "Passion & Death of Jesus",
        ignatianConnection: "Following Christ — What does it cost to follow Him daily? Learn how to apply your gifts in service and sacrifice.",
        definition: "Your unique calling — WHERE and HOW you serve",
        description: "Identity leads to mission. Every person has a unique call to live love in concrete, daily ways — at work, in relationships, in study, in service, in hidden sacrifices. Pope Francis teaches: \"You don't just HAVE a mission—you ARE a mission.\"",
        keyScripture: "\"Use your gift to serve one another, as good stewards of God's varied grace.\" — 1 Peter 4:10",
        whatYouDiscover: "Your Mission Field: Mercy (Bringing Compassion), Truth (Teaching & Illuminating), Justice (Defending & Advocating), or Gathering (Building Community). Most have a primary and secondary field.",
        selfDiscovery: {
          title: "Discover Your Mission Field",
          intro: "Pope Francis says, 'You don't just HAVE a mission—you ARE a mission.' Your unique combination of gifts, experiences, and passions points to where God is calling you to serve. Explore your mission field:",
          suggestions: [
            "Life Contexts Inventory: Where has God placed you? Family, workplace, parish, neighborhood, professional associations, online communities, volunteer organizations—each is a potential mission field.",
            "Mission Field Types: Are you drawn to Mercy (bringing compassion to the suffering), Truth (teaching and illuminating minds), Justice (defending the vulnerable and advocating for the marginalized), or Gathering (building community and bringing people together)?",
            "Professional Charism: How might your career training, education, and work experience be used for God's kingdom? What unique access do you have?",
            "Passion + Ability + Need: Where do your deepest passions (what moves your heart) meet your greatest abilities (what you do well) and the world's deepest needs (where there is suffering)?",
            "Past Service Experiences: When have you felt most alive while serving others? What were you doing? Who were you helping?",
            "Hidden Sacrifices & Daily Offerings: What small, unseen acts of love do you offer daily? These 'little way' offerings (St. Thérèse) are powerful mission."
          ]
        },
        reflection: "Two people with the same vocation can have completely different missions based on their unique design. How have you already been living your mission?"
      },
      {
        letter: "P",
        name: "Purpose",
        greek: "Omega (Ω)",
        ignatianWeek: "Fourth Stage",
        ignatianTheme: "Resurrection & God's Presence",
        ignatianConnection: "Contemplation to Attain Love — How do I love God in all things? Understand your ultimate purpose and be sent forth with joy.",
        definition: "Why it all matters — your ultimate 'why'",
        description: "Ultimately, I am made to know, love, and serve God, and to live forever with Him in heaven. Purpose gives shape, meaning, joy, and direction to my life. Your purpose is to: 1) Glorify God — Reflect His image, 2) Flourish — Become fully who He created you to be, 3) Help others flourish — Lead them to the same fullness.",
        keyScripture: "\"I came that they may have life, and have it abundantly.\" — John 10:10",
        whatYouDiscover: "Your ultimate purpose (telos/end), how your mission connects to God's eternal purposes, and the legacy you want to leave.",
        selfDiscovery: {
          title: "Clarify Your Purpose & Legacy",
          intro: "Your ultimate purpose is to glorify God, flourish as the person He created you to be, and help others do the same. St. Ignatius's 'Contemplation to Attain Love' invites us to see how all our gifts flow from God and return to Him. Reflect on your eternal calling:",
          suggestions: [
            "Legacy Meditation: At the end of your life, what do you want to be remembered for? Write your own eulogy—what story do you want told?",
            "Life Verse or Personal Mission Statement: Is there a Scripture passage that captures your life's purpose? Can you articulate your personal mission in one sentence?",
            "Patron Saints & Role Models: Which saints inspire you? What qualities do they embody that you want to cultivate? Who are your living role models?",
            "Eternal Perspective: How does your daily work—even the mundane tasks—connect to God's kingdom? How can you 'pray always' through your actions?",
            "Generativity: What are you building that will outlast you? Who are you mentoring or forming? What seeds are you planting for future generations?",
            "Ignatian Indifference: Can you hold your plans, dreams, and desires with open hands, ready to receive whatever God wills? This is the ultimate freedom."
          ]
        },
        reflection: "At the end of your life, what do you want to be known for? How does your daily mission connect to God's eternal purposes?"
      }
    ],
    quote: {
      text: "I am a mission on this earth; that is the reason why I am here in this world. We have to regard ourselves as sealed, even branded, by this mission of bringing light, blessing, enlivening, raising up, healing and freeing.",
      author: "Pope Francis, Evangelii Gaudium #273"
    },
    note: "RIMP flows in order: Relationship → Identity → Mission → Purpose. But it also becomes a lifelong cycle of continual growth. As St. Irenaeus said: \"The glory of God is man fully alive.\""
  },

  weeks: [
    {
      number: 1,
      pillar: "Relationship",
      theme: "I am loved even though I am weak at times and in need of redemption",
      focus: "Experiencing God's personal, tender mercy, the privilege and beauty of being created in God's image and likeness.",
      ignatianParallel: "First Week – Principle and Foundation: I have been created by the Creator, Mercy, disorder, and freedom.",
      grace: "To know myself as loved and held in the mercy of the Father.",
      days: [
        { 
          day: 1, 
          scripture: "Psalm 139:1-18", 
          usccbLink: "https://bible.usccb.org/bible/psalms/139",
          title: "God knows me", 
          scriptureText: "LORD, you have probed me, you know me: you know when I sit and stand; you understand my thoughts from afar. You sift through my travels and my rest; with all my ways you are familiar. Even before a word is on my tongue, LORD, you know it all. Behind and before you encircle me and rest your hand upon me... You formed my inmost being; you knit me in my mother's womb. I praise you, because I am wonderfully made; wonderful are your works! My very self you know.",
          reflection: "Reflect on how intimately God knows every detail of your life—your thoughts, your movements, your deepest fears and hopes. He formed you with intention and love." 
        },
        { 
          day: 2, 
          scripture: "Isaiah 43:1-7", 
          usccbLink: "https://bible.usccb.org/bible/isaiah/43",
          title: "You are precious in my eyes", 
          scriptureText: "But now, thus says the LORD, who created you, Jacob, and formed you, Israel: Do not fear, for I have redeemed you; I have called you by name: you are mine. When you pass through waters, I will be with you; through rivers, you shall not be swept away... Because you are precious in my eyes and honored, and I love you, I give people in return for you and nations in exchange for your life.",
          reflection: "Hear God speaking directly to you: 'You are precious in my eyes, and honored, and I love you.' Let these words penetrate your heart today." 
        },
        { 
          day: 3, 
          scripture: "Luke 15:11-32", 
          usccbLink: "https://bible.usccb.org/bible/luke/15",
          title: "The Prodigal Son", 
          scriptureText: "Then he said, 'A man had two sons, and the younger son said to his father, \"Father, give me the share of your estate that should come to me.\"... While he was still a long way off, his father caught sight of him, and was filled with compassion. He ran to his son, embraced him and kissed him... 'Let us celebrate with a feast, because this son of mine was dead, and has come to life again; he was lost, and has been found.'",
          reflection: "Place yourself in the story. Are you the younger son returning? The older son struggling? Feel the Father's embrace and unconditional love." 
        },
        { 
          day: 4, 
          scripture: "Romans 8:31-39", 
          usccbLink: "https://bible.usccb.org/bible/romans/8",
          title: "Nothing separates us from God's love", 
          scriptureText: "What then shall we say to this? If God is for us, who can be against us? He who did not spare his own Son but handed him over for us all, how will he not also give us everything else along with him?... For I am convinced that neither death, nor life, nor angels, nor principalities, nor present things, nor future things, nor powers, nor height, nor depth, nor any other creature will be able to separate us from the love of God in Christ Jesus our Lord.",
          reflection: "What are the things in your life that make you feel separated from God? Let this passage wash over those fears and doubts." 
        },
        { 
          day: 5, 
          scripture: "Mark 2:1-12", 
          usccbLink: "https://bible.usccb.org/bible/mark/2",
          title: "Healing of the paralytic", 
          scriptureText: "When Jesus returned to Capernaum after some days, it became known that he was at home... They came bringing to him a paralytic carried by four men... When Jesus saw their faith, he said to the paralytic, 'Child, your sins are forgiven.'... 'I say to you, rise, pick up your mat, and go home.' He rose, picked up his mat at once, and went away in the sight of everyone.",
          reflection: "What paralyzes you spiritually? Bring it to Jesus and hear Him say, 'Your sins are forgiven. Rise.'" 
        },
        { 
          day: 6, 
          scripture: "Matthew 9:9-13", 
          usccbLink: "https://bible.usccb.org/bible/matthew/9",
          title: "The Call of Matthew", 
          scriptureText: "As Jesus passed on from there, he saw a man named Matthew sitting at the customs post. He said to him, 'Follow me.' And he got up and followed him... He said, 'Those who are well do not need a physician, but the sick do. Go and learn the meaning of the words, \"I desire mercy, not sacrifice.\" I did not come to call the righteous but sinners.'",
          reflection: "Jesus calls Matthew, a tax collector, to follow Him. How is Jesus calling you from your current situation? What is He inviting you to leave behind?" 
        },
        { 
          day: 7, 
          scripture: "Week Review", 
          usccbLink: "https://bible.usccb.org/bible/psalms/103",
          title: "Meditative Review", 
          scriptureText: "Bless the LORD, my soul; all my being, bless his holy name! Bless the LORD, my soul; and do not forget all his gifts, Who pardons all your sins, and heals all your ills, Who redeems your life from the pit, and crowns you with mercy and compassion. — Psalm 103:1-4",
          reflection: "Look back over the week. What graces have you received? What movements of the heart have you noticed? Give thanks for God's mercy and faithfulness." 
        }
      ]
    },
    {
      number: 2,
      pillar: "Identity",
      theme: "Who is Jesus? Who am I in Him?",
      focus: "How do I imitate Him—His virtues, character, relationships with others?",
      ignatianParallel: "Second Week – Call of the King, life of Christ.",
      grace: "To know Jesus more intimately, love Him more deeply, and follow Him more closely.",
      note: "This is the heart of Regnum Christi spirituality.",
      days: [
        { 
          day: 8, 
          scripture: "Luke 1:26-38", 
          usccbLink: "https://bible.usccb.org/bible/luke/1",
          title: "The Annunciation", 
          scriptureText: "The angel Gabriel was sent from God to a town of Galilee called Nazareth, to a virgin betrothed to a man named Joseph... The angel said to her, 'Do not be afraid, Mary, for you have found favor with God. Behold, you will conceive in your womb and bear a son, and you shall name him Jesus.'... Mary said, 'Behold, I am the handmaid of the Lord. May it be done to me according to your word.'",
          reflection: "Mary's 'yes' changed everything. What is God asking you to say 'yes' to right now? What fiat is He waiting for?" 
        },
        { 
          day: 9, 
          scripture: "Luke 2:1-20", 
          usccbLink: "https://bible.usccb.org/bible/luke/2",
          title: "The Nativity", 
          scriptureText: "In those days a decree went out from Caesar Augustus that the whole world should be enrolled... While they were there, the time came for her to have her child, and she gave birth to her firstborn son. She wrapped him in swaddling clothes and laid him in a manger, because there was no room for them in the inn... The shepherds went in haste and found Mary and Joseph, and the infant lying in the manger.",
          reflection: "Imagine yourself in the stable. What do you see? What do you want to say to the infant Jesus? How does God's humility speak to you?" 
        },
        { 
          day: 10, 
          scripture: "Matthew 3:13-17", 
          usccbLink: "https://bible.usccb.org/bible/matthew/3",
          title: "Baptism of Jesus", 
          scriptureText: "Then Jesus came from Galilee to John at the Jordan to be baptized by him... After Jesus was baptized, he came up from the water and behold, the heavens were opened for him, and he saw the Spirit of God descending like a dove and coming upon him. And a voice came from the heavens, saying, 'This is my beloved Son, with whom I am well pleased.'",
          reflection: "Hear the Father's words to Jesus: 'This is my beloved Son.' Through your baptism, these words are spoken over you too. You are beloved." 
        },
        { 
          day: 11, 
          scripture: "Matthew 5:1-12", 
          usccbLink: "https://bible.usccb.org/bible/matthew/5",
          title: "The Beatitudes", 
          scriptureText: "Blessed are the poor in spirit, for theirs is the kingdom of heaven. Blessed are they who mourn, for they will be comforted. Blessed are the meek, for they will inherit the land. Blessed are they who hunger and thirst for righteousness, for they will be satisfied. Blessed are the merciful, for they will be shown mercy. Blessed are the clean of heart, for they will see God. Blessed are the peacemakers, for they will be called children of God.",
          reflection: "Which beatitude speaks most to your current situation? Why? How is Jesus calling you to live this beatitude today?" 
        },
        { 
          day: 12, 
          scripture: "Mark 1:16-20", 
          usccbLink: "https://bible.usccb.org/bible/mark/1",
          title: "Calling the Disciples", 
          scriptureText: "As he passed by the Sea of Galilee, he saw Simon and his brother Andrew casting their nets into the sea; they were fishermen. Jesus said to them, 'Come after me, and I will make you fishers of men.' Then they abandoned their nets and followed him... He saw James, the son of Zebedee, and his brother John... Then he called them. So they left their father Zebedee in the boat along with the hired men and followed him.",
          reflection: "Jesus calls ordinary people to extraordinary mission. How is He calling you to follow more closely? What nets do you need to leave behind?" 
        },
        { 
          day: 13, 
          scripture: "Mark 1:40-45", 
          usccbLink: "https://bible.usccb.org/bible/mark/1",
          title: "Jesus Heals the Leper", 
          scriptureText: "A leper came to him and kneeling down begged him and said, 'If you wish, you can make me clean.' Moved with pity, he stretched out his hand, touched him, and said to him, 'I do will it. Be made clean.' The leprosy left him immediately, and he was made clean.",
          reflection: "Jesus reaches out and touches the untouchable. What 'unclean' parts of yourself need His healing touch? Bring them to Him now." 
        },
        { 
          day: 14, 
          scripture: "Mark 4:35-41", 
          usccbLink: "https://bible.usccb.org/bible/mark/4",
          title: "Jesus Calms the Storm", 
          scriptureText: "On that day, as evening drew on, he said to them, 'Let us cross to the other side.'... A violent squall came up and waves were breaking over the boat... They woke him and said to him, 'Teacher, do you not care that we are perishing?' He woke up, rebuked the wind, and said to the sea, 'Quiet! Be still!' The wind ceased and there was great calm. Then he asked them, 'Why are you terrified? Do you not yet have faith?'",
          reflection: "What storms are raging in your life? Hear Jesus say, 'Peace! Be still!' to your anxious heart. Trust in His power over every storm." 
        },
        { 
          day: 15, 
          scripture: "John 6:1-15", 
          usccbLink: "https://bible.usccb.org/bible/john/6",
          title: "Multiplication of Loaves", 
          scriptureText: "When Jesus raised his eyes and saw that a large crowd was coming to him, he said to Philip, 'Where can we buy enough food for them to eat?'... Andrew, the brother of Simon Peter, said to him, 'There is a boy here who has five barley loaves and two fish; but what good are these for so many?' Jesus said, 'Have the people recline.'... Then Jesus took the loaves, gave thanks, and distributed them to those who were reclining.",
          reflection: "Jesus takes what little we offer and multiplies it abundantly. What are you holding back from Him? What small offering can you bring?" 
        },
        { 
          day: 16, 
          scripture: "John 11:1-44", 
          usccbLink: "https://bible.usccb.org/bible/john/11",
          title: "Raising of Lazarus", 
          scriptureText: "When Mary came to where Jesus was and saw him, she fell at his feet and said to him, 'Lord, if you had been here, my brother would not have died.' When Jesus saw her weeping and the Jews who had come with her weeping, he became perturbed and deeply troubled... And Jesus wept... Jesus cried out in a loud voice, 'Lazarus, come out!' The dead man came out.",
          reflection: "Jesus weeps with those who weep. He has power over death itself. What needs resurrection in your life? What is dead that needs His life-giving word?" 
        },
        { 
          day: 17, 
          scripture: "Matthew 17:1-9", 
          usccbLink: "https://bible.usccb.org/bible/matthew/17",
          title: "The Transfiguration", 
          scriptureText: "Jesus took Peter, James, and John his brother, and led them up a high mountain by themselves. And he was transfigured before them; his face shone like the sun and his clothes became white as light... A bright cloud cast a shadow over them, then from the cloud came a voice that said, 'This is my beloved Son, with whom I am well pleased; listen to him.'",
          reflection: "The disciples glimpse Jesus's divine glory. Ask for the grace to see Christ's glory in your daily life, even in ordinary moments." 
        },
        { 
          day: 18, 
          scripture: "Contemplation", 
          usccbLink: "https://bible.usccb.org/bible/philippians/3",
          title: "Contemplation of Christ's Identity", 
          scriptureText: "I consider everything as a loss because of the supreme good of knowing Christ Jesus my Lord. For his sake I have accepted the loss of all things and I consider them so much rubbish, that I may gain Christ and be found in him. — Philippians 3:8-9",
          reflection: "Who is Jesus to you now? How has your understanding of Him grown this week? Journal your thoughts and thank God for revealing Himself to you." 
        }
      ]
    },
    {
      number: 3,
      pillar: "Mission",
      theme: "Love is proved in fire",
      focus: "The challenge we face with the Cross! How do we handle our daily crosses, sufferings, challenges? Even the work we love can be difficult. Even the people we love can be challenging to live and work with. Do we unite these challenges with Christ—do we bring them to the cross to be cleansed so we can be nurtured by His wounds?",
      ignatianParallel: "Third Week – The Passion.",
      grace: "To be with Jesus in His suffering with courageous love.",
      days: [
        { 
          day: 19, 
          scripture: "John 13:1-17", 
          usccbLink: "https://bible.usccb.org/bible/john/13",
          title: "Washing of Feet", 
          scriptureText: "Before the feast of Passover, Jesus knew that his hour had come to pass from this world to the Father. He loved his own in the world and he loved them to the end... He rose from supper and took off his outer garments. He took a towel and tied it around his waist. Then he poured water into a basin and began to wash the disciples' feet... 'If I, therefore, the master and teacher, have washed your feet, you ought to wash one another's feet.'",
          reflection: "Jesus, the Lord of all, kneels to wash feet. What acts of humble service is He inviting you to? Who needs your servant leadership today?" 
        },
        { 
          day: 20, 
          scripture: "Luke 22:39-46", 
          usccbLink: "https://bible.usccb.org/bible/luke/22",
          title: "Agony in the Garden", 
          scriptureText: "Then going out he went, as was his custom, to the Mount of Olives, and the disciples followed him... He withdrew about a stone's throw from them, knelt, and prayed, saying, 'Father, if you are willing, take this cup away from me; still, not my will but yours be done.' And to strengthen him an angel from heaven appeared to him. He was in such agony and he prayed so fervently that his sweat became like drops of blood falling on the ground.",
          reflection: "Stay awake with Jesus in His agony. What causes Him anguish? What causes you anguish? Bring your struggles to this holy moment." 
        },
        { 
          day: 21, 
          scripture: "Matthew 26:47-56", 
          usccbLink: "https://bible.usccb.org/bible/matthew/26",
          title: "Betrayal and Arrest", 
          scriptureText: "While he was still speaking, Judas, one of the Twelve, arrived, accompanied by a large crowd, with swords and clubs... The betrayer had arranged a sign with them, saying, 'The man I shall kiss is the one; arrest him.' Immediately he went over to Jesus and said, 'Hail, Rabbi!' and he kissed him. Jesus answered him, 'Friend, do what you have come for.'",
          reflection: "Judas betrays with a kiss. How do we sometimes betray Jesus with our words and actions? Ask for His mercy and the grace to remain faithful." 
        },
        { 
          day: 22, 
          scripture: "John 18:28-40", 
          usccbLink: "https://bible.usccb.org/bible/john/18",
          title: "Trial Before Pilate", 
          scriptureText: "Then Pilate went back into the praetorium and summoned Jesus and said to him, 'Are you the King of the Jews?'... Jesus answered, 'My kingdom does not belong to this world.'... Pilate said to him, 'Then you are a king?' Jesus answered, 'You say I am a king. For this I was born and for this I came into the world, to testify to the truth. Everyone who belongs to the truth listens to my voice.'",
          reflection: "Jesus stands before Pilate in dignified silence and truth. What injustices in your life call for patient endurance? How can you witness to truth?" 
        },
        { 
          day: 23, 
          scripture: "John 19:16-30", 
          usccbLink: "https://bible.usccb.org/bible/john/19",
          title: "The Crucifixion", 
          scriptureText: "So they took Jesus, and carrying the cross himself he went out to what is called the Place of the Skull, in Hebrew, Golgotha. There they crucified him... When Jesus had taken the wine, he said, 'It is finished.' And bowing his head, he handed over the spirit.",
          reflection: "Stand at the foot of the cross. Look upon Jesus. Let Him look upon you. What passes between you in this sacred moment?" 
        },
        { 
          day: 24, 
          scripture: "John 19:25-27", 
          usccbLink: "https://bible.usccb.org/bible/john/19",
          title: "Mary at the Cross", 
          scriptureText: "Standing by the cross of Jesus were his mother and his mother's sister, Mary the wife of Clopas, and Mary of Magdala. When Jesus saw his mother and the disciple there whom he loved, he said to his mother, 'Woman, behold, your son.' Then he said to the disciple, 'Behold, your mother.' And from that hour the disciple took her into his home.",
          reflection: "Mary stands faithfully by her Son in His darkest hour. Ask her to teach you how to stand firm in suffering, and to take you as her own child." 
        },
        { 
          day: 25, 
          scripture: "Luke 23:50-56", 
          usccbLink: "https://bible.usccb.org/bible/luke/23",
          title: "Burial of Jesus", 
          scriptureText: "Now there was a virtuous and righteous man named Joseph who, though he was a member of the council, had not consented to their plan of action. He came from the Jewish town of Arimathea and was awaiting the kingdom of God. He went to Pilate and asked for the body of Jesus. After he had taken the body down, he wrapped it in a linen cloth and laid him in a rock-hewn tomb in which no one had yet been buried.",
          reflection: "Rest in the silence of Holy Saturday. What needs to die in you before new life can emerge? What must be buried with Christ?" 
        }
      ]
    },
    {
      number: 4,
      pillar: "Purpose",
      theme: "Resurrection joy & apostolic life",
      focus: "Unless there is purpose to our sufferings, why should we suffer? Christianity promises us a beautiful kingdom in eternity with the One who knows us most intimately. We are reminded this week that our ultimate purpose is to know, love, and serve God and be with Him in eternity, and share this Good News with as many others as possible!",
      ignatianParallel: "Fourth Week – Resurrection & Contemplation to Attain Love.",
      grace: "To see God in all things and to serve Him with joy.",
      days: [
        { 
          day: 26, 
          scripture: "John 20:1-18", 
          usccbLink: "https://bible.usccb.org/bible/john/20",
          title: "The Empty Tomb", 
          scriptureText: "On the first day of the week, Mary of Magdala came to the tomb early in the morning, while it was still dark, and saw the stone removed from the tomb... Jesus said to her, 'Mary!' She turned and said to him in Hebrew, 'Rabbouni,' which means Teacher. Jesus said to her, 'Stop holding on to me, for I have not yet ascended to the Father. But go to my brothers and tell them...'",
          reflection: "Mary Magdalene meets the Risen Lord and He calls her by name. Where are you looking for Jesus? Where is He actually waiting for you?" 
        },
        { 
          day: 27, 
          scripture: "Luke 24:13-35", 
          usccbLink: "https://bible.usccb.org/bible/luke/24",
          title: "Road to Emmaus", 
          scriptureText: "That very day two of them were going to a village seven miles from Jerusalem called Emmaus... While they were conversing and debating, Jesus himself drew near and walked with them, but their eyes were prevented from recognizing him... And it happened that, while he was with them at table, he took bread, said the blessing, broke it, and gave it to them. With that their eyes were opened and they recognized him... 'Were not our hearts burning within us while he spoke to us on the way?'",
          reflection: "Jesus walks with the disciples and their hearts burn. Where is Jesus walking with you unrecognized? Where do you encounter Him in the breaking of bread?" 
        },
        { 
          day: 28, 
          scripture: "John 21:1-14", 
          usccbLink: "https://bible.usccb.org/bible/john/21",
          title: "Breakfast with Jesus", 
          scriptureText: "When it was already dawn, Jesus was standing on the shore; but the disciples did not realize that it was Jesus. Jesus said to them, 'Children, have you caught anything to eat?'... When they climbed out on shore, they saw a charcoal fire with fish on it and bread. Jesus said to them, 'Come, have breakfast.' None of the disciples dared to ask him, 'Who are you?' because they realized it was the Lord.",
          reflection: "Jesus prepares breakfast for His tired friends. He nourishes us for mission. What nourishment do you need from the Lord right now?" 
        },
        { 
          day: 29, 
          scripture: "Matthew 28:16-20", 
          usccbLink: "https://bible.usccb.org/bible/matthew/28",
          title: "The Great Commission", 
          scriptureText: "The eleven disciples went to Galilee, to the mountain to which Jesus had ordered them. When they saw him, they worshiped, but they doubted. Then Jesus approached and said to them, 'All power in heaven and on earth has been given to me. Go, therefore, and make disciples of all nations, baptizing them in the name of the Father, and of the Son, and of the holy Spirit, teaching them to observe all that I have commanded you. And behold, I am with you always, until the end of the age.'",
          reflection: "Go and make disciples. What is your unique mission? How is Jesus sending you? Remember: 'I am with you always.'" 
        },
        { 
          day: 30, 
          scripture: "1 John 4:7-21", 
          usccbLink: "https://bible.usccb.org/bible/1john/4",
          title: "God is Love", 
          scriptureText: "Beloved, let us love one another, because love is of God; everyone who loves is begotten by God and knows God. Whoever is without love does not know God, for God is love. In this way the love of God was revealed to us: God sent his only Son into the world so that we might have life through him... There is no fear in love, but perfect love drives out fear... We love because he first loved us.",
          reflection: "Perfect love casts out fear. How has this retreat deepened your understanding of God's love? How will you share this love with others? You ARE a mission!" 
        }
      ]
    }
  ],

  examen: {
    title: "The Ignatian Examens",
    subtitle: "Midday Examen & Nightly Examen — Essential daily practices",
    intro: "St. Ignatius considered the Examen the most important prayer after the Mass. This retreat includes TWO examen moments: a brief Midday Examen (5 min) to pause and notice God's movements, insights, and struggles in your day, and a Nightly Examen (10-15 min) to review your day the Ignatian Way. Both are prayerful conversations with God about your day (Dia-Logos).",
    steps: [
      {
        number: 1,
        title: "Gratitude — Become aware of God's presence",
        description: "Slow down. Thank God for today's blessings. Ask the Holy Spirit to help you see the day through God's eyes."
      },
      {
        number: 2,
        title: "Petition — Review the day with gratitude",
        description: "Ask for light to see your day clearly. Walk through the past 24 hours. Notice gifts, moments of grace, people who blessed you, joys and surprises."
      },
      {
        number: 3,
        title: "Review — Pay attention to your interior movements",
        description: "Walk through your day hour by hour. Where did you feel peace? Joy? Consolation? Anxiety? Desolation? Interior resistance? These movements are the 'language of God' in Ignatian spirituality."
      },
      {
        number: 4,
        title: "Sorrow — Ask forgiveness and receive mercy",
        description: "Gently acknowledge areas where you fell short—without shame. Ask forgiveness for failures. Let Jesus draw close to you with mercy."
      },
      {
        number: 5,
        title: "Resolution — Look ahead to tomorrow with hope",
        description: "Ask God for the specific grace you need for the next day's challenges. Entrust everything to His guidance."
      }
    ],
    note: "The Midday Examen and Nightly Examen are essential to discernment and interior freedom."
  },

  discernmentPillars: {
    title: "The Top Five Discernment Pillars of St. Ignatius",
    subtitle: "Core principles for making good decisions and following God's will",
    intro: "These timeless discernment tools help you recognize how God speaks in the heart:",
    pillars: [
      {
        number: 1,
        title: "Consolation & Desolation",
        description: "Consolation: movements toward God—peace, joy, clarity, trust, love, increase of faith, hope, and charity. Desolation: movements away from God—discouragement, confusion, heaviness, darkness of soul, turmoil.",
        rule: "Move with consolation. Stand firm and do not change decisions in desolation. In time of desolation, never make a change."
      },
      {
        number: 2,
        title: "Freedom from Disordered Attachments",
        description: "Ask: What holds me back from choosing the good? Attachments can be fears, habits, compulsions, wounds, or comforts. Freedom allows you to desire what God desires. True indifference means being open to whatever God wants."
      },
      {
        number: 3,
        title: "The Principle & Foundation (Purpose of Life)",
        description: "I am created to praise, reverence, and serve God—and by this means to save my soul. Everything else is secondary and only helpful insofar as it leads me to God. This gives clarity to all decision-making."
      },
      {
        number: 4,
        title: "The Three 'Times' for Making a Decision",
        description: "St. Ignatius identifies three moments when discernment is possible:",
        times: [
          { name: "Time 1: Clear Grace", description: "God moves the soul so directly that the right choice is obvious—like Paul on the road to Damascus." },
          { name: "Time 2: Movements of Consolation and Desolation", description: "You weigh interior movements in prayer over time, noticing where God gives peace and where there is resistance." },
          { name: "Time 3: Reasoned, Quiet Reflection", description: "When no strong movements exist, use spiritual logic: 'Which option leads me to greater love of God and neighbor?'" }
        ]
      },
      {
        number: 5,
        title: "The Test of the Fruit",
        description: "A choice from God leads over time to: deeper peace, generosity, charity, humility, growth in virtue, and alignment with Scripture and Church teaching. As Jesus said, 'By their fruits you will know them.'",
        warning: "If the fruit is chaos, fear, self-enclosure, or distance from God and others, reconsider the choice."
      }
    ]
  },

  quotes: [
    { text: "I am a mission on this earth; that is the reason why I am here in this world.", author: "Pope Francis" },
    { text: "The glory of God is man fully alive.", author: "St. Irenaeus" },
    { text: "Not all of us can do great things. But we can do small things with great love.", author: "St. Mother Teresa" },
    { text: "Before I formed you in the womb I knew you, before you were born I set you apart.", author: "Jeremiah 1:5" }
  ],

  // Music for Prayer - organized by week
  musicForPrayer: {
    title: "Music for Prayer & Meditation",
    subtitle: "Sacred music to accompany your retreat journey",
    intro: "Music can be a powerful aid to prayer, helping quiet the mind and open the heart to God. Below are carefully selected pieces for each week of the retreat, spanning classical, contemporary Christian, and jazz traditions.",
    weeks: [
      {
        weekNumber: 1,
        theme: "The Principle and Foundation / Mercy",
        description: "Music to help you rest in God's mercy and reflect on your place in His creation.",
        tracks: [
          { artist: "Gregorio Allegri", title: "Miserere Mei, Deus", genre: "Classical (Choral)", youtubeId: "36Y_ztEW1NE" },
          { artist: "W.A. Mozart", title: "Kyrie (Mass in C Minor)", genre: "Classical (Choral)", youtubeId: "HNOrSxpSNHg" },
          { artist: "Arvo Pärt", title: "Spiegel im Spiegel", genre: "Classical (Minimalist)", youtubeId: "TJ6Mzvh3XCc" },
          { artist: "Monks of Silos", title: "Ubi Caritas (Gregorian Chant)", genre: "Gregorian Chant", youtubeId: "FpVheKD9xdE" },
          { artist: "Duke Ellington & Mahalia Jackson", title: "Come Sunday", genre: "Jazz / Gospel", youtubeId: "cInnyr5hlhE" },
          { artist: "Francesca LaRosa", title: "Anima Christi", genre: "Contemporary Sacred", youtubeId: "rxE1aWb8Lxw" }
        ]
      },
      {
        weekNumber: 2,
        theme: "The Life of Christ / The Call",
        description: "Music to accompany you as you walk with Jesus and discern His call.",
        tracks: [
          { artist: "J.S. Bach", title: "Jesu, Joy of Man's Desiring", genre: "Classical (Baroque)", youtubeId: "S6OgZCCoXWc" },
          { artist: "Vaughan Williams", title: "The Lark Ascending", genre: "Classical (Chamber)", youtubeId: "ZR2JlDnT2l8" },
          { artist: "César Franck", title: "Panis Angelicus", genre: "Classical (Vocal)", youtubeId: "zEUThBIKPXI" },
          { artist: "Leeland", title: "Way Maker (Live)", genre: "Modern Christian", youtubeId: "iJCV_2H9xD0" },
          { artist: "Dave Brubeck", title: "Take Five", genre: "Sacred Jazz", youtubeId: "vmDDOFXSgAs" },
          { artist: "Matt Maher", title: "Lord, I Need You", genre: "Acoustic Worship", youtubeId: "LuvfMDhTyMA" }
        ]
      },
      {
        weekNumber: 3,
        theme: "The Passion",
        description: "Music to help you enter into Christ's suffering with courageous love.",
        tracks: [
          { artist: "G.B. Pergolesi", title: "Stabat Mater", genre: "Classical (Oratorio)", youtubeId: "qzOmPUu-F_M" },
          { artist: "J.S. Bach", title: "St. Matthew Passion (Opening)", genre: "Classical (Baroque)", youtubeId: "RnkSFKJ4rC0" },
          { artist: "Gabriel Fauré", title: "Pie Jesu (Requiem)", genre: "Classical (Requiem)", youtubeId: "dHbUebcQopo" },
          { artist: "Hillsong United", title: "Lead Me to the Cross", genre: "Modern Christian", youtubeId: "J7PA8v7z9Fk" },
          { artist: "Bill Evans", title: "Peace Piece", genre: "Jazz (Piano)", youtubeId: "Nv2GgV34qIg" },
          { artist: "Taizé Community", title: "Stay With Me", genre: "Contemplative Chant", youtubeId: "-OHPoVhwgnM" }
        ]
      },
      {
        weekNumber: 4,
        theme: "Resurrection / Finding God in All Things",
        description: "Music of joy and gratitude as you celebrate the Risen Lord.",
        tracks: [
          { artist: "G.F. Handel", title: "Hallelujah Chorus", genre: "Classical (Baroque)", youtubeId: "IUZEtVbJT5c" },
          { artist: "King's College Choir", title: "Ave Verum Corpus (Mozart)", genre: "Classical (Choral)", youtubeId: "pscsAvGjQI0" },
          { artist: "Beethoven", title: "Ode to Joy (Symphony No. 9)", genre: "Classical (Symphonic)", youtubeId: "rOjHhS5MtvA" },
          { artist: "Brandon Lake", title: "Gratitude", genre: "Modern Christian", youtubeId: "1em6MJp0zUc" },
          { artist: "Louis Armstrong", title: "What a Wonderful World", genre: "Jazz / Gospel", youtubeId: "rBrd_3VMC3c" },
          { artist: "John Foley, S.J.", title: "One Bread, One Body", genre: "Folk-Liturgical", youtubeId: "tLlpHSxLMCc" }
        ]
      }
    ]
  },

  // Spiritual Talks & Meditations - organized by week
  spiritualTalks: {
    title: "Spiritual Talks & Meditations",
    subtitle: "Retreat Guides featuring Fr. John Bartunek, LC",
    intro: "These retreat guides are professionally produced by Regnum Christi featuring Fr. John Bartunek, LC. Each includes video, audio, PDF, and a personal questionnaire. Use them as supplements to your daily meditation or to prepare for each week.",
    weeks: [
      {
        weekNumber: 1,
        theme: "First Week: Foundation & Mercy",
        description: "Retreat guides on God's mercy, our identity, and recognizing our need for redemption.",
        talks: [
          { 
            title: "Father of Mercies: The Prodigal Son", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A Lenten retreat guide exploring God's mercy through the parable of the Prodigal Son.",
            link: "https://www.regnumchristi.com/en/father-of-mercies-a-lenten-retreat-guide-on-the-parable-of-the-prodigal-son/",
            youtubeId: "0hiILW1X1Pc",
            duration: "Retreat Guide"
          },
          { 
            title: "From Sorrow to Joy: Confession", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A retreat guide on the Sacrament of Confession and experiencing God's forgiveness.",
            link: "https://www.regnumchristi.com/en/from-sorrow-to-joy-a-retreat-guide-on-the-sacrament-of-confession/",
            youtubeId: "ISGfy5x-DU8",
            duration: "Retreat Guide"
          },
          { 
            title: "Who You Are: Baptism & Identity", 
            speaker: "Fr. John Bartunek, LC", 
            description: "Rediscover the truth about who we are through the Sacrament of Baptism—our spiritual DNA.",
            link: "https://www.regnumchristi.com/en/who-you-are-a-retreat-guide-on-the-sacrament-of-baptism/",
            youtubeId: "Jg5CiUJ5VIc",
            duration: "Retreat Guide"
          }
        ]
      },
      {
        weekNumber: 2,
        theme: "Second Week: The Life of Christ",
        description: "Retreat guides on vocation, discipleship, and knowing Jesus through Gospel contemplation.",
        talks: [
          { 
            title: "Called and Chosen: St. Matthew's Vocation", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A retreat guide on vocation and the calling of St. Matthew—hearing Christ's personal invitation.",
            link: "https://www.regnumchristi.com/en/called-and-chosen-a-retreat-guide-on-vocation-and-the-calling-of-st-matthew/",
            youtubeId: "DubJEHoJs_0",
            duration: "Retreat Guide"
          },
          { 
            title: "The Complete Christian: The Twelve Apostles", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A retreat guide on the calling of the Twelve Apostles and what it means to follow Christ completely.",
            link: "https://www.regnumchristi.com/en/the-complete-christian-a-retreat-guide-on-the-calling-of-the-twelve-apostles/",
            youtubeId: "UkKKyKzpMV4",
            duration: "Retreat Guide"
          },
          { 
            title: "Miracles of Mercy: Healing Touch of Jesus", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A retreat guide on Jesus's healing miracles and His desire to heal us in body and soul.",
            link: "https://www.regnumchristi.com/en/miracles-of-mercy-a-retreat-guide-on-the-healing-touch-of-jesus/",
            youtubeId: "23LpXA89FDY",
            duration: "Retreat Guide"
          }
        ]
      },
      {
        weekNumber: 3,
        theme: "Third Week: The Passion",
        description: "Retreat guides on the Cross, suffering with Christ, and Mary's sorrow.",
        talks: [
          { 
            title: "The Colors of the Cross: Lent", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A Lenten retreat guide meditating on the meaning and colors of Christ's Cross.",
            link: "https://www.regnumchristi.com/en/the-colors-of-the-cross-a-retreat-guide-for-lent/",
            youtubeId: "TZSqpVdZw-8",
            duration: "Retreat Guide"
          },
          { 
            title: "Teacher and Lord: The Last Supper", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A Lenten retreat guide on the Last Supper and Jesus washing the disciples' feet.",
            link: "https://www.regnumchristi.com/en/teacher-and-lord-a-lenten-retreat-guide-on-the-last-supper/",
            youtubeId: "L3LLz-vVvK8",
            duration: "Retreat Guide"
          },
          { 
            title: "A Mother's Tears: Our Lady of Sorrows", 
            speaker: "Fr. John Bartunek, LC", 
            description: "A retreat guide on Our Lady of Sorrows and uniting our suffering with Mary at the Cross.",
            link: "https://www.regnumchristi.com/en/a-mothers-tears-a-retreat-guide-on-our-lady-of-sorrows/",
            youtubeId: "dWpfTjATx1I",
            duration: "Retreat Guide"
          }
        ]
      },
      {
        weekNumber: 4,
        theme: "Fourth Week: Resurrection & Divine Love",
        description: "Retreat guides on Easter joy, Divine Mercy, and victory in Christ.",
        talks: [
          { 
            title: "A Cure for Discouragement: Easter", 
            speaker: "Fr. John Bartunek, LC", 
            description: "An Easter retreat guide on overcoming discouragement through the Risen Christ.",
            link: "https://www.regnumchristi.com/en/a-cure-for-discouragement-a-retreat-guide-for-easter/",
            youtubeId: "mmAEnwp1nm4",
            duration: "Retreat Guide"
          },
          { 
            title: "The Dawn of Mercy: Divine Mercy", 
            speaker: "Fr. John Bartunek, LC", 
            description: "An Easter retreat guide on Divine Mercy Sunday and God's infinite mercy.",
            link: "https://www.regnumchristi.com/en/the-dawn-of-mercy-an-easter-retreat-guide-on-divine-mercy/",
            youtubeId: "ikdSHjBNSEs",
            duration: "Retreat Guide"
          },
          { 
            title: "The Taste of Victory: Easter", 
            speaker: "Fr. John Bartunek, LC", 
            description: "An Easter retreat guide on experiencing the victory of Christ's Resurrection.",
            link: "https://www.regnumchristi.com/en/the-taste-of-victory-a-retreat-guide-for-easter/",
            youtubeId: "0hiILW1X1Pc",
            duration: "Retreat Guide"
          }
        ]
      }
    ]
  }
};
